import { Graphics, Sprite, Texture } from "pixi.js"
import { Coordinate, TileSize } from "game-common/models"
import { floorElevation, STOREY_RISE_PX } from "game-common/room/ember/two_storey_house"
import { STAIR_LADDER_HEIGHT } from "game-common/stair_travel"
import { storeyCellAlpha } from "./storey_visibility"
import { stairTileAt } from "game-common/room/ember/stairs"
import { TileMapMetaProvider } from "game-common/tile_map_meta_provider"

export interface StackedLadderState {
    kind: "bottom" | "middle" | "top" | "isolated"
    height: number
    hatch?: boolean
    ceiling?: boolean
}

/** Choose artwork from the live connections, including edits on the adjacent floors. */
export const stackedLadderState = (map: TileMapMetaProvider, at: Coordinate): StackedLadderState | undefined => {
    const delta = map.getMapDeltaAt(at.x, at.y, "sprite2")
    const links = delta?.context?.context?.stairLandings ?? delta?.context?.stairLandings
    if (delta?.removed || !links || !stairTileAt(map, at)) return undefined
    const upper = map.getUpperFloorProvider(), lower = map.getFloorSupportProvider()
    // Local links determine the shape. Companion floors can arrive before their route
    // context, so requiring a reciprocal landing briefly adds a second hatch at the foot.
    // A loaded neighbour with its ladder removed still disconnects the artwork.
    const connected = (neighbor: TileMapMetaProvider | undefined, roomId: string) =>
        !neighbor || neighbor.roomId !== roomId || !!stairTileAt(neighbor, at)
    const up = !!links.up && connected(upper, links.up.roomId)
    const down = !!links.down && connected(lower, links.down.roomId)
    const rise = upper?.twoStoreyHouse && map.twoStoreyHouse
        ? floorElevation(upper.twoStoreyHouse) - floorElevation(map.twoStoreyHouse)
        : (map.twoStoreyHouse?.rise ?? 1) * STOREY_RISE_PX
    const hatch = down && up && ladderPassesFloor(map, at)
    const ownRoof = map.resolveTileMapFromLayerId("sky")?.[at.x]?.[at.y]
    const upperFloor = upper?.resolveTileMapFromLayerId("map2")?.[at.x]?.[at.y]
    const throughCeiling = up && (!!ownRoof && ownRoof !== "0" || !!upperFloor && upperFloor !== "0")
    // A roof cutaway hides the neighbouring ladder sections with the upper room.
    // In that view, show the whole interior flight instead of just one compressed storey.
    const ceilingView = throughCeiling && storeyCellAlpha(links.up.roomId, at.x, at.y) < 1
    return { ...(ceilingView ? { ceiling: true } : {}), ...(hatch ? { hatch: true } : {}), kind: up ? down ? "middle" : "bottom" : down ? "top" : "isolated",
        height: Math.max(8, Math.round(rise)) }
}

/** A flight crossing this storey's floor, or the ceiling under it, keeps a hatch rim there: the opening it climbs through. */
export const ladderPassesFloor = (map: TileMapMetaProvider, at: Coordinate): boolean => {
    const surface = map.resolveTileMapFromLayerId("map2")?.[at.x]?.[at.y]
    const ceiling = map.getFloorSupportProvider()?.resolveTileMapFromLayerId("sky")?.[at.x]?.[at.y]
    return !!surface && surface !== "0" || !!ceiling && ceiling !== "0"
}

/** How much rail a flight's top hatch shows below its rim: the stub an extension grows from. */
export const LADDER_TOP_STUB_PX = 6

export interface LadderPixelRect { x: number; y: number; width: number; height: number; color: number }

// Upper hatch rim from art/ladder-up/ladder-up.png, in its original wood palette.
const hatchPalette = [0x1a120c, 0x24170f, 0x4a3222, 0x5c3e28, 0x6e4a30, 0x8f6440, 0xb08050, 0xd2a46c]
const hatchRim = [
    "          0000000000000000000000000         ",
    "         077677666666766666767675550        ",
    "        06660553555555555555552506660       ",
    "       0777213314444434333333323137770      ",
    "       0666310000000000000000000136660      ",
    "      077730                    0236770     ",
    "     0666630                    02277760    ",
    "     4777312 730             077 0266664    ",
    "    056662107740             07700225665    ",
    "   06555554076304555555555553076405555550   ",
    "   06555554076304555555555554076405555550   ",
    "   05444444076304444444444444076404444440   ",
    "    000000007620000000000000007640000000    ",
    "           0761              07640          ",
]

/** How tall a hatch's rim is drawn, down from the top of the opening to the floor it is cut in. */
export const LADDER_HATCH_RIM_PX = hatchRim.length

/** Pixel-aligned wood artwork, using the existing ladder's palette. Baseline is y=0.
 * Each rising section ends exactly where the next floor's section begins.
 */
export const stackedLadderPixels = ({ kind, height, hatch, ceiling }: StackedLadderState): LadderPixelRect[] => {
    const pixels: LadderPixelRect[] = []
    const rect = (x: number, y: number, width: number, h: number, color: number) =>
        pixels.push({ x, y, width, height: h, color })
    const top = kind === "top" ? -LADDER_TOP_STUB_PX : -Math.max(height, ceiling ? STAIR_LADDER_HEIGHT : 0)
    const capY = ceiling ? top : kind === "isolated" ? -height : -hatchRim.length
    const rims = kind === "top" || kind === "isolated" || hatch || ceiling
        ? ceiling && hatch ? [capY, -hatchRim.length] : [capY] : []
    // Transparent pixels inside the rim would show intact floorboards. Recess the
    // opening behind the rails and rungs, including the terminating and ceiling hatches.
    for (const rimY of rims) {
        rect(3, rimY + 4, 27, 10, 0x1a120c)
        rect(4, rimY + 4, 25, 2, 0x100d09)
    }
    // Match the original 64px frame: its 27px rail body sits at x=3..29,
    // with the 44px visible foot silhouette centered at x=16.
    for (const x of [3, 24]) {
        rect(x, top, 6, -top, 0x1a120c)
        rect(x + 1, top, 4, -top, 0x8f6440)
        rect(x + 1, top, 2, -top, 0xb08050)
        rect(x + 1, top, 1, -top, 0xd2a46c)
        rect(x + 4, top, 1, -top, 0x5c3e28)
        // Broken highlights and darker grain keep the original's hand-hewn character.
        // Leave both ends unchanged so neighbouring sections still join exactly.
        for (let y = top + 5; y < -5; y += 16) {
            rect(x + 1, y, 1, 5, 0x8f6440)
            rect(x + 3, y + 2, 1, 3, 0x6e4a30)
        }
    }
    for (let y = -6; y >= top; y -= 8) {
        rect(7, y, 19, 4, 0x24170f)
        rect(7, y, 19, 2, 0xb08050)
        rect(8, y, 16, 1, 0xd2a46c)
        rect(7, y + 2, 19, 1, 0x6e4a30)
        rect(4, y + 1, 3, 1, 0xb08050)
        rect(26, y + 1, 3, 1, 0x8f6440)
    }
    if (kind === "bottom" || kind === "isolated") {
        // Original-style splayed braces only at the foot, never repeated at a seam.
        for (let i = 0; i < 9; i++) {
            const y = -11 + i
            rect(2 - i, y, 4, 2, 0x24170f)
            rect(26 + i, y, 4, 2, 0x24170f)
            rect(3 - i, y, 2, 1, 0xb08050)
            rect(27 + i, y, 2, 1, 0x8f6440)
        }
        rect(-6, -2, 7, 2, 0x1a120c)
        rect(31, -2, 7, 2, 0x1a120c)
        rect(-5, -2, 5, 1, 0xb08050)
        rect(32, -2, 5, 1, 0xb08050)
        // A worn, broad bottom rung, as on the freestanding original.
        rect(5, -5, 22, 3, 0x5c3e28)
        rect(6, -5, 20, 1, 0xb08050)
        for (const x of [8, 13, 21]) rect(x, -4, 2, 1, 0x6e4a30)
    }
    // Keep the original chunky cheeks and bevels around the recessed opening.
    rims.forEach(rimY => hatchRim.forEach((row, y) => {
        for (let x = 0; x < row.length;) {
            const shade = row[x], start = x++
            while (x < row.length && row[x] === shade) x++
            if (shade !== " ") rect(start - 6, rimY + y, x - start, 1, hatchPalette[Number(shade)])
        }
    }))
    return pixels
}

/** An owned entity-tile sprite; its small graphic is redrawn only when a connection changes. */
export class StackedLadderSprite extends Sprite {
    readonly heightBlurExempt = true
    private readonly art = new Graphics()
    private artworkKey = ""
    constructor(private readonly map: TileMapMetaProvider, private readonly at: Coordinate) {
        super(Texture.EMPTY)
        this.anchor.set(0, 1)
        this.addChild(this.art)
        this.pose()
    }
    pose = (): void => {
        const state = stackedLadderState(this.map, this.at)
        this.x = this.at.x * TileSize
        this.y = (this.at.y + 1) * TileSize
        const key = state ? `${state.kind}:${state.height}:${!!state.hatch}:${!!state.ceiling}` : ""
        if (key === this.artworkKey) return
        this.artworkKey = key
        this.art.clear()
        if (!state) return
        for (const pixel of stackedLadderPixels(state)) {
            this.art.beginFill(pixel.color).drawRect(pixel.x, pixel.y, pixel.width, pixel.height).endFill()
        }
    }
}
